import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    HostListener,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ElementRef
} from '@angular/core';
import { Title } from '@angular/platform-browser';

import { Subscription, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { CsvImportDialogService, CsvField, ImportedFile } from '../../../../Common/Dialogs/components/CsvImportDialog/csv-import-dialog.service';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { CsvContactApiModel } from '../../../../api/targeting/model/csv-contact-api.model';
import { ContactImportFileParameters, ContactImportParameters, ContactImportableFields } from '../../services/contact-csv-import-dialog.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ListPickerDialogComponent } from '../../../../Common/RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { ChipModel } from '../../../../Common/Forms/models/chip.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { TargetingListList } from '../../models/targeting-list-list.model';
import { TabsComponent } from '../../../../Common/Layout/components/Tabs/tabs.component';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { allowedFileTypes, FileUploadModel } from '../../../../Common/Forms/models/file-upload.model';
import { CsvImporterComponent } from '../../../../Common/TargetingShared/components/CsvImporter/csv-importer.component';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { AutocompleteChipsOptions } from '../../../../Common/Forms/components/AutocompleteChips/autocomplete-chips.component';
import { TargetingListService } from '../../services/targeting-list.service';
import { FocusHelper } from '../../../../Common/Forms/helpers/focus.helper';


export enum ImportListAction {
    individual,
    list,
    listPerFile
}

interface FieldEntry {
    field: CsvField<CsvContactApiModel>;
    fieldIndex: number;
}

interface SingleFieldDisplayRow {
    kind: 'single';
    field: CsvField<CsvContactApiModel>;
    fieldIndex: number;
}

export interface RadioGroupDisplayRow {
    kind: 'radioGroup';
    groupLabel: string;
    required: boolean;
    options: FieldEntry[][];
    selectedOptionIndex: number;
}

type DisplayRow = SingleFieldDisplayRow | RadioGroupDisplayRow;

@Component({
    selector: 'agpr-contact-importer',
    templateUrl: './contact-importer.component.html',
    styleUrls: ['./contact-importer.component.scss'],
    standalone: false
})
export class ContactImporterComponent extends CsvImporterComponent<CsvContactApiModel, ContactImportFileParameters> implements OnInit, OnDestroy {

    @Output() hideContactImporter: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild(TabsComponent, {static: false}) tabs: TabsComponent = new TabsComponent(this.el);

    loading: boolean = false;
    overlayMode: boolean = true;

    selectedImportListAction: ImportListAction = ImportListAction.individual;

    initialFileList: FileUploadModel[] = [];
    parameters: ContactImportParameters = new ContactImportParameters();

    listsForAllFiles: TargetingListList[] = [];
    listsForAllFilesChips: ChipModel[] = [];
    listsPerFileChips: { [key: string]: TargetingListList[]} = {};

    csvColumnDropdownOptions: DropdownOptionModel[][] = [];
    selectedOptions: DropdownOptionModel[][] = [];
    displayRowsPerFile: DisplayRow[][] = [];

    private oldTitle: string;
    private subscriptions: Subscription[] = [];

    private readonly NAME_GROUP_FIELD_IDS = new Set([
        ContactImportableFields.firstName,
        ContactImportableFields.lastName,
        ContactImportableFields.firstLast,
        ContactImportableFields.lastFirst,
    ]);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    readonly FILE_LIMIT = 10;
    readonly id = 'csvImport';
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly importListActions = ImportListAction;
    readonly listPickerButton = {
        tooltip: '',
        icon: 'fa-regular fa-duotone fa-gear'
    };
    readonly listAutocompleteOptions: AutocompleteChipsOptions = {
        suggestionsFetcher: this.listFetcher.bind(this),
        delay: 300,
        minLength: 3,
        forceSelection: false,
        clearOnBlur: true
    };
    readonly csvMimeFileTypes: string = [
        ...allowedFileTypes.csvMIMETypesRegex.split('|').map(x => x[x.length - 1] === '$' ? x.substring(0, x.length - 1) : x)
    ].join(',');

    constructor(
        public coreDialogService: CoreDialogService,
        public csvImportDialogService: CsvImportDialogService<CsvContactApiModel, ContactImportFileParameters>,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public currentUserService: CurrentUserStateService,
        public titleService: Title,
        public cd: ChangeDetectorRef,
        public targetingListService: TargetingListService,
        private el: ElementRef,
    ) {
        super(
            csvImportDialogService,
            coreDialogService,
            messageService,
            localizationService,
            cd
        );
        this.csvImportDialogService.initialize();
        this.csvImportDialogService.setFieldDefinitions();
        this.oldTitle = this.titleService.getTitle();
        this.titleService.setTitle(
            this.localizationService.transform('Discover.importContacts.browserTitle')
        );
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.csvImportDialogService.importedFiles$.subscribe(x => {
                this.csvFiles = x;
                this.csvFiles.forEach(file => this.listsPerFileChips[file.fileName] = this.listsPerFileChips[file.fileName] || []);
                this.updateDropdownOptions();
                this.buildDisplayRows();
                this.cd.detectChanges();
            }),
            this.currentUserService.getUser().subscribe(user => {
                if (!this.parameters.reportDestinationEmails.find(email => email === user.email)) {
                    this.parameters.reportDestinationEmails.push(user.email);
                }
            })
        );
    }

    @HostListener('window:keyup.escape') onKeyDown() {
        if (this.coreDialogService.hasOpenedDialogs()) {
            return;
        }
        this.hideContactImporter.emit();
    }

    filesSelected(event: Event) {
        const files = (event.target as HTMLInputElement).files;

        if (!files) {
            return;
        }

        if (files.length + this.csvFiles.length > this.FILE_LIMIT) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: this.localizationService.transform(
                    'Discover.csvImporter.errors.tooManyFiles',
                    { files: this.FILE_LIMIT }
                )
            });
            return;
        }

        this.readFilesToCsvDialogService(files, true).subscribe(successfulFiles => {
            this.initialFileList = successfulFiles.map(file => ({
                name: file.name,
                size: file.size,
                type: file.type,
                file: file
            }));
        });

        (event.target as HTMLInputElement).value = ''; // clear with an empty string, null would result in 'null'
    }

    // TODO : line up with reqs
    import() {
        const validationErrors = this.csvImportDialogService.getValidationErrors();
        this.csvFiles.forEach(file => {
            file.fileParameters.noListsError = this.listsPerFileChips[file.fileName].length === 0 && !file.fileParameters.createNewListWithFilename;
        });

        if (validationErrors.length > 0) {
            validationErrors.forEach(error => {
                this.messageService.add({severity : messageSeverityEnum.error, detail: error});
            });
        } else if (this.parameters.reportDestinationEmails.length === 0) {
            this.messageService.add({
                severity: messageSeverityEnum.error,
                detail: this.localizationService.transform('Discover.contactImporter.errors.missingRecipient')
            });
        } else {
            if (this.selectedImportListAction === ImportListAction.list) {
                // Make sure there's no lingering state
                this.csvFiles.forEach(file => {
                    file.fileParameters.lists = this.listsForAllFiles;
                    file.fileParameters.createNewListWithFilename = false;
                });
            } else if (this.selectedImportListAction === ImportListAction.listPerFile) {
                if (this.csvFiles.some(file => file.fileParameters.noListsError)) {
                    FocusHelper.focusFirstError(this.el.nativeElement, '.agpr-form-message');

                    return;
                }
            } else {
                // Make sure there's no lingering state
                this.csvFiles.forEach(file => {
                    file.fileParameters.lists = [];
                    file.fileParameters.createNewListWithFilename = false;
                });
            }
            this.showConfirmImportDialog();
        }
    }

    openListPicker() {
        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                initialSelectedLists: this.listsForAllFiles,
                showListBuilderCheckbox: false,
                addMode: false,
            },
            outputs: {
                newSelectedLists: (lists: TargetingListList[]) => {
                    this.listsForAllFiles = lists;
                    this.listsForAllFilesChips = lists.map(x => {
                        return {
                            label: x.name ?? ''
                        };
                    });
                }
            }
        });
    }

    openListPickerForFile(file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>) {
        this.coreDialogService.open(ListPickerDialogComponent, {
            inputs: {
                initialSelectedLists: file.fileParameters.lists,
                showListBuilderCheckbox: false,
                addMode: false,
            },
            outputs: {
                newSelectedLists: (lists: TargetingListList[]) => {
                    file.fileParameters.lists = lists;
                    this.listsPerFileChips[file.fileName] = lists;
                }
            }
        });
    }

    fileRemoved(removedFile: FileUploadModel) {
        const fileToRemove = this.csvFiles.find(file => file.fileName === removedFile.name);
        if(fileToRemove) {
            this.removeFile(fileToRemove);
        }
    }

    filesAdded(addedFiles: FileUploadModel[]) {
        const filesToAdd = addedFiles.map(f => f.file).filter((f): f is File => f != null);
        this.readFilesToCsvDialogService(filesToAdd, false);
    }

    fileValidationErrors(files: FileUploadModel[]) {
        const fileNames = files.map(file => file.name ?? '');
        this.showFailedFilesDialog(fileNames);
    }

    /** toggle selected list error message off when mode changes to create list with filename */
    onCreateList(value: boolean, file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>) {
        if (value) {
            file.fileParameters.noListsError = false;
        }
    }

    onSelectListChange(chips: TargetingListList[], file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>) {
        if (chips.length > 0) {
            file.fileParameters.noListsError = false;
        }
    }

    private removeFile(file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>) {
        const fileIndex = this.csvFiles.findIndex(theFile => theFile === file);
        this.csvFiles.splice(fileIndex, 1);

        if (this.tabs._selectedTabIndex === fileIndex) {
            // @HACK The splicing's effects on the tabs won't be instantaneous, let's wait a bit to select a new tab
            setTimeout(() => {
                this.tabs?.selectTabByIndex(0);
            }, 100);
        }
    }

    closeImporter() {
        this.csvImportDialogService.clearImportedFiles();
        this.hideContactImporter.emit();
    }

    onDropdownChanged(
        option: DropdownOptionModel,
        file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>,
        field: CsvField<CsvContactApiModel>
    ) {
        field.targetColumn = option.value;
        this.csvImportDialogService.updateExampleValue(file, field);
    }

    onNameRadioChanged(
        optionIndex: number,
        file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>,
        fileIndex: number,
        row: DisplayRow
    ): void {
        if (row.kind !== 'radioGroup') {
            return;
        }
        row.options.forEach((option, idx) => {
            if (idx !== optionIndex) {
                option.forEach(({ field, fieldIndex }) => {
                    const ignoreOption = this.csvColumnDropdownOptions[fileIndex]?.[0];
                    if (ignoreOption) {
                        field.targetColumn = ignoreOption.value;
                        this.csvImportDialogService.updateExampleValue(file, field);
                        this.selectedOptions[fileIndex][fieldIndex] = ignoreOption;
                    }
                });
            }
        });
    }

    private buildDisplayRows(): void {
        this.displayRowsPerFile = this.csvFiles.map((file, fileIndex) =>
            this.buildDisplayRowsForFile(file, fileIndex)
        );
    }

    private buildDisplayRowsForFile(
        file: ImportedFile<CsvContactApiModel, ContactImportFileParameters>,
        fileIndex: number
    ): DisplayRow[] {
        const rows: DisplayRow[] = [];
        let nameGroupInserted = false;

        const nameFieldsByFieldId = new Map<number, FieldEntry>();
        file.csvFields.forEach((field, fieldIndex) => {
            if (this.NAME_GROUP_FIELD_IDS.has(field.originalField.fieldId)) {
                nameFieldsByFieldId.set(field.originalField.fieldId, { field, fieldIndex });
            }
        });

        let nameGroupRow: RadioGroupDisplayRow | null = null;
        if (nameFieldsByFieldId.size > 0) {
            const fnEntry = nameFieldsByFieldId.get(ContactImportableFields.firstName);
            const lnEntry = nameFieldsByFieldId.get(ContactImportableFields.lastName);
            const flEntry = nameFieldsByFieldId.get(ContactImportableFields.firstLast);
            const lfEntry = nameFieldsByFieldId.get(ContactImportableFields.lastFirst);

            const options: FieldEntry[][] = [];
            const pairOption = [fnEntry, lnEntry].filter((e): e is FieldEntry => !!e);
            if (pairOption.length > 0) {
                options.push(pairOption);
            }
            if (flEntry) {
                options.push([flEntry]);
            }
            if (lfEntry) {
                options.push([lfEntry]);
            }

            // Preserve the user's in-flight selection across rebuilds (e.g. when another file is added)
            const existing = this.displayRowsPerFile[fileIndex]?.find(
                (r): r is RadioGroupDisplayRow => r.kind === 'radioGroup'
            );

            let selectedOptionIndex = 0;
            if (existing) {
                selectedOptionIndex = existing.selectedOptionIndex;
            } else if (flEntry?.field.targetColumn) {
                selectedOptionIndex = options.findIndex(opt => opt[0] === flEntry);
            } else if (lfEntry?.field.targetColumn) {
                selectedOptionIndex = options.findIndex(opt => opt[0] === lfEntry);
            }

            nameGroupRow = {
                kind: 'radioGroup',
                groupLabel: 'Discover.contactImporter.fields.name',
                required: true,
                options,
                selectedOptionIndex,
            };
        }

        file.csvFields.forEach((field, fieldIndex) => {
            if (this.NAME_GROUP_FIELD_IDS.has(field.originalField.fieldId)) {
                if (!nameGroupInserted && nameGroupRow) {
                    rows.push(nameGroupRow);
                    nameGroupInserted = true;
                }
            } else {
                rows.push({ kind: 'single', field, fieldIndex });
            }
        });

        return rows;
    }

    unselectListChip(index: number) {
        this.listsForAllFilesChips.splice(index, 1);
        this.listsForAllFiles.splice(index, 1);
    }

    showConfirmImportDialog() {
        const io: ConfirmDialogModel = {
            inputs: {
                id: 'contactImporter.confirmImportDialog',
                heading: 'Discover.contactImporter.confirmImportDialog.header',
                message: 'Discover.contactImporter.confirmImportDialog.message',
                list: this.parameters.reportDestinationEmails,
                okLabel: 'Discover.contactImporter.confirmImportDialog.acknowledge',
                showCancel: false
            },
            outputs: {
                dialogClose: confirm => {
                    if (confirm) {
                        this.csvImportDialogService.importData(this.parameters).subscribe(done => {
                            this.messageService.add({
                                severity: messageSeverityEnum.success,
                                detail: this.localizationService.transform('Discover.contactImporter.success.importQueued')
                            });
                            this.closeImporter();
                        });
                    }
                }
            }
        };

        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    private listFetcher(query: string): Observable<TargetingListList[]> {
        if (!query) {
            return this.targetingListService.all$;
        } else {
            return this.targetingListService.all$.pipe(
                map(lists => lists.filter(list => list.name?.toLocaleLowerCase().includes(query.toLocaleLowerCase()))));
        }
    }

    ngOnDestroy() {
        this.csvImportDialogService.clearImportedFiles();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.titleService.setTitle(this.oldTitle);
    }
}
